import React, { useState, useEffect, useCallback } from "react";
import {
  Container,
  Title,
  Grid,
  Paper,
  Text,
  Group,
  Badge,
  Table,
  ScrollArea,
  Tabs,
  Switch,
  TextInput,
  Loader,
  Center,
  Alert,
  Pagination,
  Select,
  Button,
  Textarea,
  Stack,
} from "@mantine/core";
import { showNotification, updateNotification } from "@mantine/notifications";
import { DatePickerInput } from "@mantine/dates";
import { useDebouncedValue } from "@mantine/hooks";
import dayjs from "dayjs";
import {
  IconMessageCircle,
  IconAlertTriangle,
  IconBrandWhatsapp,
  IconSettings,
  IconChartBar,
  IconList,
  IconUsers,
  IconBrush,
  IconHelp,
  IconActivity,
  IconDatabase,
} from "@tabler/icons-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { useResibotApi } from "../../../hooks/useResibotApi";

function buildDailyChartSeries(days, points) {
  const byDate = new Map((points || []).map((p) => [p.date, p]));
  const out = [];
  for (let i = days - 1; i >= 0; i -= 1) {
    const d = dayjs().subtract(i, "day").format("YYYY-MM-DD");
    const row = byDate.get(d);
    out.push({
      date: d,
      label: dayjs(d).format("MMM D"),
      queries: row?.queries ?? 0,
      fallbacks: row?.fallbacks ?? 0,
    });
  }
  return out;
}

function cleaningStatusDisplay(status) {
  const s = String(status || "").toLowerCase();
  if (s === "pending") return { label: "Pending", color: "yellow" };
  if (s === "overdue") return { label: "Overdue", color: "red" };
  if (s === "done") return { label: "Completed", color: "green" };
  return { label: status || "—", color: "gray" };
}

function StatCard({ title, value, icon: Icon, color, subtitle }) {
  return (
    <Paper shadow="sm" p="lg" radius="md" withBorder>
      <Group position="apart">
        <div>
          <Text size="xs" color="dimmed" transform="uppercase" weight={700}>
            {title}
          </Text>
          <Text size="xl" weight={700} mt={4}>
            {value}
          </Text>
          {subtitle && (
            <Text size="xs" color="dimmed" mt={4}>
              {subtitle}
            </Text>
          )}
        </div>
        <Icon size={32} stroke={1.5} color={color || "#4a8078"} />
      </Group>
    </Paper>
  );
}

function humanError(err) {
  if (!err?.message) return "Something went wrong.";
  const s = err.status;
  if (s === 401) {
    return "Sign-in expired or access was denied. Please sign in again.";
  }
  if (s === 403) {
    return "You don't have permission for this. Contact your administrator if it continues.";
  }
  if (s === 503) {
    return "The service is temporarily unavailable. Please try again later.";
  }
  return err.message;
}

function OverviewTab({ request }) {
  const [rangeDays, setRangeDays] = useState("30");
  const [stats, setStats] = useState(null);
  const [dailyPoints, setDailyPoints] = useState([]);
  const [recentQueries, setRecentQueries] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const daysNum = Number(rangeDays) || 30;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const qs = (params) =>
      new URLSearchParams(
        Object.entries(params).filter(([, v]) => v !== undefined && v !== "")
      ).toString();

    Promise.all([
      request(`/api/analytics/summary?${qs({ days: daysNum })}`),
      request(`/api/analytics/daily?${qs({ days: daysNum })}`),
      request(`/api/analytics/recent?${qs({ limit: 10, days: daysNum })}`),
    ])
      .then(([summary, daily, recent]) => {
        if (cancelled) return;
        setStats({
          totalQueries: summary?.total_queries ?? 0,
          fallbackRate: summary?.fallback_rate ?? 0,
          avgResponseMs: summary?.avg_response_time_ms ?? 0,
          fallbackCount: summary?.fallback_count ?? 0,
          whatsappContacts: summary?.contacts_count ?? 0,
        });
        setDailyPoints(daily?.points || []);
        setRecentQueries(recent?.queries || []);
      })
      .catch((e) => {
        if (!cancelled) setError(humanError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [request, daysNum]);

  const lineData = buildDailyChartSeries(daysNum, dailyPoints);
  const answeredCount = Math.max(
    0,
    (stats?.totalQueries ?? 0) - (stats?.fallbackCount ?? 0)
  );
  const donutData = [
    { name: "Answered (no fallback)", value: answeredCount, color: "#4a8078" },
    { name: "Fallback", value: stats?.fallbackCount ?? 0, color: "#e03131" },
  ].filter((d) => d.value > 0);

  if (loading) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <>
      {error && (
        <Alert color="red" title="Could not load overview" mb="md" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Group position="right" align="flex-end" mb="md" wrap="wrap">
        <Select
          label="Period"
          w={200}
          data={[
            { value: "7", label: "Last 7 days" },
            { value: "30", label: "Last 30 days" },
            { value: "90", label: "Last 90 days" },
          ]}
          value={rangeDays}
          onChange={(v) => setRangeDays(v || "30")}
        />
      </Group>
      <Grid mb="xl">
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Queries answered"
            value={stats?.totalQueries ?? 0}
            icon={IconMessageCircle}
            color="#4a8078"
            subtitle={`Rolling ${daysNum} days`}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Fallback rate"
            value={`${(stats?.fallbackRate ?? 0).toFixed(1)}%`}
            icon={IconAlertTriangle}
            color={stats?.fallbackRate > 20 ? "#e03131" : "#f59f00"}
            subtitle={`Rolling ${daysNum} days`}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Avg response time"
            value={
              stats?.avgResponseMs != null
                ? `${Math.round(stats.avgResponseMs)} ms`
                : "—"
            }
            icon={IconActivity}
            color="#228be6"
            subtitle={`Rolling ${daysNum} days`}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
          <StatCard
            title="WhatsApp contacts"
            value={stats?.whatsappContacts ?? 0}
            icon={IconBrandWhatsapp}
            color="#fd7e14"
            subtitle="Distinct handoff sessions (fallback) in window"
          />
        </Grid.Col>
      </Grid>

      <Grid mb="xl">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Paper shadow="sm" p="lg" radius="md" withBorder h={360}>
            <Text size="sm" weight={600} mb="md">
              Queries per day
            </Text>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={lineData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e9ecef" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={40} />
                <RechartsTooltip />
                <Line type="monotone" dataKey="queries" name="Queries" stroke="#4a8078" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Paper shadow="sm" p="lg" radius="md" withBorder h={360}>
            <Text size="sm" weight={600} mb="xs">
              Answered vs fallback
            </Text>
            {(stats?.totalQueries ?? 0) === 0 ? (
              <Center h={280}>
                <Text color="dimmed" size="sm">
                  No queries in this period
                </Text>
              </Center>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <PieChart>
                  <Pie
                    data={donutData.length ? donutData : [{ name: "No data", value: 1, color: "#dee2e6" }]}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={58}
                    outerRadius={88}
                    paddingAngle={2}
                  >
                    {(donutData.length ? donutData : [{ color: "#dee2e6" }]).map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <RechartsTooltip />
                </PieChart>
              </ResponsiveContainer>
            )}
          </Paper>
        </Grid.Col>
      </Grid>

      <Paper shadow="sm" p="lg" radius="md" withBorder>
        <Title order={4} mb="md">
          Recent Queries
        </Title>
        <ScrollArea h={400}>
          <Table striped highlightOnHover>
            <thead>
              <tr>
                <th>Guest / query</th>
                <th>Response</th>
                <th>Fallback</th>
                <th>Time</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {recentQueries.map((q) => (
                <tr key={q.id || q.session_id + (q.query_timestamp || "")}>
                  <td style={{ maxWidth: 220 }}>
                    <Stack spacing={4}>
                      {(q.visitor_name || q.visitor_email) && (
                        <Text size="xs" weight={600} color="dimmed">
                          {[q.visitor_name, q.visitor_email].filter(Boolean).join(" · ")}
                        </Text>
                      )}
                      <Text size="sm" lineClamp={2}>
                        {q.query_message || q.user_query}
                      </Text>
                    </Stack>
                  </td>
                  <td style={{ maxWidth: 250 }}>
                    <Text size="sm" lineClamp={2}>
                      {q.bot_response}
                    </Text>
                  </td>
                  <td>
                    <Badge color={q.fallback_triggered ? "red" : "green"} size="sm">
                      {q.fallback_triggered ? "Yes" : "No"}
                    </Badge>
                  </td>
                  <td>
                    <Text size="sm">{q.response_time_ms}ms</Text>
                  </td>
                  <td>
                    <Text size="xs" color="dimmed">
                      {q.query_timestamp
                        ? new Date(q.query_timestamp).toLocaleDateString()
                        : "-"}
                    </Text>
                  </td>
                </tr>
              ))}
              {recentQueries.length === 0 && (
                <tr>
                  <td colSpan={5}>
                    <Text color="dimmed" align="center">
                      No queries yet
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </ScrollArea>
      </Paper>
    </>
  );
}

function QueriesTab({ request, tenantId }) {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState("");
  const [debounced] = useDebouncedValue(search, 400);
  const [fallbackOnly, setFallbackOnly] = useState(false);
  const [timeMode, setTimeMode] = useState("90");
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const [queries, setQueries] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams({
      page: String(page),
      limit: String(limit),
      tenant_id: tenantId,
    });
    if (debounced) qs.set("search", debounced);
    if (fallbackOnly) qs.set("fallback_only", "true");

    if (timeMode === "custom") {
      if (customFrom) qs.set("date_from", dayjs(customFrom).format("YYYY-MM-DD"));
      if (customTo) qs.set("date_to", dayjs(customTo).format("YYYY-MM-DD"));
    } else if (timeMode !== "all") {
      const days = Number(timeMode);
      qs.set(
        "date_from",
        dayjs().subtract(days, "day").format("YYYY-MM-DD")
      );
    }

    try {
      const data = await request(`/api/analytics/queries?${qs.toString()}`);
      setQueries(data.queries || []);
      setTotal(data.total ?? 0);
      const p = data.pages;
      setPages(typeof p === "number" ? p : 0);
    } catch (e) {
      setError(humanError(e));
      setQueries([]);
      setPages(0);
    } finally {
      setLoading(false);
    }
  }, [
    request,
    tenantId,
    page,
    limit,
    debounced,
    fallbackOnly,
    timeMode,
    customFrom,
    customTo,
  ]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Paper shadow="sm" p="lg" radius="md" withBorder>
      {error && (
        <Alert color="red" mb="md" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Group mb="md" grow align="flex-end">
        <TextInput
          label="Search queries"
          placeholder="Substring match on user question…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <Select
          label="Time window"
          data={[
            { value: "30", label: "Last 30 days" },
            { value: "90", label: "Last 90 days" },
            { value: "365", label: "Last 365 days" },
            { value: "all", label: "All time" },
            { value: "custom", label: "Custom range" },
          ]}
          value={timeMode}
          onChange={(v) => {
            setTimeMode(v || "90");
            setPage(1);
          }}
        />
        <Select
          label="Filter"
          data={[
            { value: "all", label: "All queries" },
            { value: "fallback", label: "Fallback only" },
          ]}
          value={fallbackOnly ? "fallback" : "all"}
          onChange={(v) => {
            setFallbackOnly(v === "fallback");
            setPage(1);
          }}
        />
      </Group>
      {timeMode === "custom" && (
        <Group mb="md" grow>
          <DatePickerInput
            label="From"
            value={customFrom}
            onChange={(d) => {
              setCustomFrom(d);
              setPage(1);
            }}
            maxDate={customTo || undefined}
          />
          <DatePickerInput
            label="To"
            value={customTo}
            onChange={(d) => {
              setCustomTo(d);
              setPage(1);
            }}
            minDate={customFrom || undefined}
          />
        </Group>
      )}
      <Text size="sm" color="dimmed" mb="sm">
        Total matching: {total}
      </Text>
      {loading ? (
        <Center p="xl">
          <Loader />
        </Center>
      ) : (
        <>
          <ScrollArea h={520}>
            <Table striped highlightOnHover>
              <thead>
                <tr>
                  <th>Guest</th>
                  <th>Query</th>
                  <th>Response</th>
                  <th>Fallback</th>
                  <th>Time (ms)</th>
                  <th>Model</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {queries.map((q) => (
                  <tr key={q.id}>
                    <td style={{ maxWidth: 120 }}>
                      <Text size="xs" lineClamp={2}>
                        {q.visitor_name || q.visitor_email || "—"}
                      </Text>
                    </td>
                    <td style={{ maxWidth: 160 }}>
                      <Text size="sm" lineClamp={2}>
                        {q.query_message || q.user_query}
                      </Text>
                    </td>
                    <td style={{ maxWidth: 220 }}>
                      <Text size="sm" lineClamp={2}>
                        {q.bot_response}
                      </Text>
                    </td>
                    <td>
                      <Badge color={q.fallback_triggered ? "red" : "green"} size="sm">
                        {q.fallback_triggered ? "Yes" : "No"}
                      </Badge>
                    </td>
                    <td>
                      <Text size="sm">{q.response_time_ms}</Text>
                    </td>
                    <td>
                      <Text size="xs">{q.llm_model || "-"}</Text>
                    </td>
                    <td>
                      <Text size="xs" color="dimmed">
                        {q.query_timestamp
                          ? new Date(q.query_timestamp).toLocaleString()
                          : "-"}
                      </Text>
                    </td>
                  </tr>
                ))}
                {queries.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7}>
                      <Text color="dimmed" align="center" py="md">
                        No queries match these filters.
                      </Text>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </ScrollArea>
          {total > 0 && pages > 1 ? (
            <Group position="center" mt="md">
              <Pagination value={page} onChange={setPage} total={pages} />
            </Group>
          ) : null}
        </>
      )}
    </Paper>
  );
}

function FaqsTab({ request, tenantId }) {
  const [topFaqs, setTopFaqs] = useState([]);
  const [unanswered, setUnanswered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams({
      tenant_id: tenantId,
      limit: "25",
    });
    request(`/api/analytics/faqs?${qs.toString()}`)
      .then((data) => {
        if (!cancelled) {
          setTopFaqs(data.top_faqs || []);
          setUnanswered(data.unanswered || []);
        }
      })
      .catch((e) => {
        if (!cancelled) setError(humanError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [request, tenantId]);

  if (loading) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Grid>
      {error && (
        <Grid.Col span={12}>
          <Alert color="red" mb="md">
            {error}
          </Alert>
        </Grid.Col>
      )}
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <Title order={4} mb="md">
            Top FAQs (by frequency)
          </Title>
          <ScrollArea h={420}>
            <Table striped highlightOnHover>
              <thead>
                <tr>
                  <th>Question theme</th>
                  <th>Count</th>
                  <th>Fallbacks</th>
                </tr>
              </thead>
              <tbody>
                {topFaqs.map((row, i) => (
                  <tr key={`top-${i}-${row.question}`}>
                    <td>
                      <Text size="sm" lineClamp={3}>
                        {row.question}
                      </Text>
                    </td>
                    <td>{row.count}</td>
                    <td>
                      <Badge color={row.fallback_count > 0 ? "orange" : "green"}>
                        {row.fallback_count}
                      </Badge>
                    </td>
                  </tr>
                ))}
                {topFaqs.length === 0 && (
                  <tr>
                    <td colSpan={3}>
                      <Text color="dimmed" align="center">
                        No FAQ clusters yet
                      </Text>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </ScrollArea>
        </Paper>
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 6 }}>
        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <Title order={4} mb="md">
            Unanswered / high-fallback themes
          </Title>
          <Text size="xs" color="dimmed" mb="sm">
            Themes where guests still triggered fallback — prioritize knowledge updates.
          </Text>
          <ScrollArea h={420}>
            <Table striped>
              <thead>
                <tr>
                  <th>Question theme</th>
                  <th>Count</th>
                  <th>Fallbacks</th>
                </tr>
              </thead>
              <tbody>
                {unanswered.map((row, i) => (
                  <tr key={`un-${i}-${row.question}`}>
                    <td>
                      <Text size="sm" lineClamp={3}>
                        {row.question}
                      </Text>
                    </td>
                    <td>{row.count}</td>
                    <td>{row.fallback_count}</td>
                  </tr>
                ))}
                {unanswered.length === 0 && (
                  <tr>
                    <td colSpan={3}>
                      <Text color="dimmed" align="center">
                        No unanswered clusters in this window
                      </Text>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </ScrollArea>
        </Paper>
      </Grid.Col>
    </Grid>
  );
}

function ContactsTab({ request, tenantId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    request(`/api/analytics/contacts?tenant_id=${encodeURIComponent(tenantId)}&limit=100`)
      .then((data) => {
        if (!cancelled) setRows(data.contacts || []);
      })
      .catch((e) => {
        if (!cancelled) setError(humanError(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [request, tenantId]);

  if (loading) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }

  return (
    <Paper shadow="sm" p="lg" radius="md" withBorder>
      {error && (
        <Alert color="red" mb="md">
          {error}
        </Alert>
      )}
      <Title order={4} mb="md">
        WhatsApp / fallback contacts
      </Title>
      <Text size="sm" color="dimmed" mb="sm">
        When the widget collects a lead, guest name and email are parsed from the query log.
      </Text>
      <ScrollArea h={560}>
        <Table striped highlightOnHover>
          <thead>
            <tr>
              <th>Guest</th>
              <th>Email</th>
              <th>Query</th>
              <th>Session</th>
              <th>Repeat</th>
              <th>Count</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((c, idx) => (
              <tr
                key={c.id || `${c.session_id || "s"}-${idx}-${c.query_timestamp || ""}`}
                style={
                  c.repeat_contact
                    ? { backgroundColor: "rgba(253, 126, 20, 0.12)" }
                    : undefined
                }
              >
                <td style={{ maxWidth: 100 }}>
                  <Text size="xs" lineClamp={2}>
                    {c.visitor_name || "—"}
                  </Text>
                </td>
                <td style={{ maxWidth: 140 }}>
                  <Text size="xs" lineClamp={1}>
                    {c.visitor_email || "—"}
                  </Text>
                </td>
                <td style={{ maxWidth: 200 }}>
                  <Text size="sm" lineClamp={2}>
                    {c.query_message || c.user_query}
                  </Text>
                </td>
                <td>
                  <Text size="xs" lineClamp={1}>
                    {c.session_id || "—"}
                  </Text>
                </td>
                <td>
                  <Badge color={c.repeat_contact ? "orange" : "gray"}>
                    {c.repeat_contact ? "Repeat" : "Once"}
                  </Badge>
                </td>
                <td>{c.contact_count ?? 1}</td>
                <td>
                  <Text size="xs" color="dimmed">
                    {c.query_timestamp
                      ? new Date(c.query_timestamp).toLocaleString()
                      : "—"}
                  </Text>
                </td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr>
                <td colSpan={7}>
                  <Text color="dimmed" align="center">
                    No fallback contacts recorded
                  </Text>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </ScrollArea>
    </Paper>
  );
}

function CleaningTab({ request, tenantId }) {
  const [status, setStatus] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const qs = new URLSearchParams({ tenant_id: tenantId, limit: "100" });
    if (status) qs.set("status", status);
    request(`/api/cleaning/jobs?${qs.toString()}`)
      .then((data) => setJobs(data.jobs || []))
      .catch((e) => setError(humanError(e)))
      .finally(() => setLoading(false));
  }, [request, tenantId, status]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Paper shadow="sm" p="lg" radius="md" withBorder>
      {error && (
        <Alert color="red" mb="md">
          {error}
        </Alert>
      )}
      <Group mb="md">
        <Select
          label="Status"
          clearable
          placeholder="All"
          data={[
            { value: "pending", label: "Pending" },
            { value: "done", label: "Done" },
            { value: "overdue", label: "Overdue" },
          ]}
          value={status}
          onChange={setStatus}
          style={{ maxWidth: 200 }}
        />
        <Button variant="light" onClick={load} mt={24}>
          Refresh
        </Button>
      </Group>
      {loading ? (
        <Center p="xl">
          <Loader />
        </Center>
      ) : (
        <ScrollArea h={520}>
          <Table striped>
            <thead>
              <tr>
                <th>Checkout</th>
                <th>Status</th>
                <th>Notes</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => {
                const st = cleaningStatusDisplay(j.status);
                return (
                  <tr key={j.id}>
                    <td>{j.checkout_date || "—"}</td>
                    <td>
                      <Badge color={st.color} variant="filled">
                        {st.label}
                      </Badge>
                    </td>
                    <td>
                      <Text size="sm" lineClamp={2}>
                        {j.notes || "—"}
                      </Text>
                    </td>
                    <td>
                      <Text size="xs">{j.updated_at || j.created_at || "—"}</Text>
                    </td>
                  </tr>
                );
              })}
              {jobs.length === 0 && (
                <tr>
                  <td colSpan={4}>
                    <Text color="dimmed" align="center">
                      No cleaning jobs
                    </Text>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </ScrollArea>
      )}
    </Paper>
  );
}

function syncFormFromConfig(c) {
  return {
    welcome_message: c.welcome_message ?? "",
    whatsapp_number: c.whatsapp_number ?? "",
    calendar_url: c.calendar_url ?? "",
    accommodation_name: c.accommodation_name ?? "",
    primary_color: c.primary_color ?? "#4a8078",
    fallback_message: c.fallback_message ?? "",
    locale: c.locale ?? "en",
  };
}

function SettingsTab({ request, tenantId }) {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(() => syncFormFromConfig({}));
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [ingestLoading, setIngestLoading] = useState(false);
  const [ingestMessage, setIngestMessage] = useState(null);
  const [ragStatus, setRagStatus] = useState(null);
  const [widgetInfo, setWidgetInfo] = useState(null);

  const loadRagStatus = useCallback(() => {
    request(`/api/rag/status/${encodeURIComponent(tenantId)}`)
      .then((s) => setRagStatus(s))
      .catch(() => setRagStatus(null));
  }, [request, tenantId]);

  const fetchConfig = useCallback(() => {
    setLoading(true);
    setError(null);
    request(`/api/config/${encodeURIComponent(tenantId)}`)
      .then((data) => {
        setConfig(data);
        setForm(syncFormFromConfig(data));
      })
      .catch((e) => setError(humanError(e)))
      .finally(() => setLoading(false));
  }, [request, tenantId]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    loadRagStatus();
  }, [loadRagStatus]);

  useEffect(() => {
    request(`/api/widget/config/${encodeURIComponent(tenantId)}`, { skipAuth: true })
      .then((w) => setWidgetInfo(w))
      .catch(() => setWidgetInfo(null));
  }, [request, tenantId]);

  const triggerKnowledgeRefresh = () => {
    setIngestMessage(null);
    setIngestLoading(true);
    request("/api/rag/trigger-update", {
      method: "POST",
      body: { tenant_id: tenantId },
    })
      .then((r) => {
        setIngestMessage(
          r.message ||
            `Status: ${r.status || "ok"} — chunks: ${r.chunk_count ?? 0}, sources: ${r.source_count ?? 0}`
        );
        loadRagStatus();
      })
      .catch((e) => setIngestMessage(humanError(e)))
      .finally(() => setIngestLoading(false));
  };

  const handleEnabledToggle = (event) => {
    if (!config || toggling) return;
    const next = event.currentTarget.checked;
    const previous = config.enabled;
    const nid = "resibot-enabled";
    setConfig((c) => ({ ...c, enabled: next }));
    setToggling(true);
    showNotification({
      id: nid,
      loading: true,
      title: "Updating ResiBot…",
      message: "",
      autoClose: false,
      disallowClose: true,
    });
    request(`/api/config/${encodeURIComponent(tenantId)}`, {
      method: "PUT",
      body: { enabled: next },
    })
      .then((data) => {
        setConfig(data);
        updateNotification({
          id: nid,
          loading: false,
          title: "Saved",
          message: next ? "ResiBot is enabled for this tenant." : "ResiBot is disabled.",
          color: "teal",
          autoClose: 4000,
          disallowClose: false,
        });
      })
      .catch((e) => {
        setConfig((c) => ({ ...c, enabled: previous }));
        updateNotification({
          id: nid,
          loading: false,
          title: "Could not update",
          message: humanError(e),
          color: "red",
          autoClose: 8000,
          disallowClose: false,
        });
      })
      .finally(() => setToggling(false));
  };

  const handleSave = () => {
    if (!config) return;
    const body = {
      welcome_message: form.welcome_message,
      whatsapp_number: form.whatsapp_number,
      calendar_url: form.calendar_url,
      accommodation_name: form.accommodation_name,
      primary_color: form.primary_color,
      fallback_message: form.fallback_message,
      locale: form.locale,
    };
    const prevSnapshot = config;
    const optimistic = { ...config, ...body };
    setConfig(optimistic);
    const nid = "resibot-config-fields";
    setSaving(true);
    showNotification({
      id: nid,
      loading: true,
      title: "Saving settings…",
      message: "",
      autoClose: false,
      disallowClose: true,
    });
    request(`/api/config/${encodeURIComponent(tenantId)}`, {
      method: "PUT",
      body,
    })
      .then((data) => {
        setConfig(data);
        setForm(syncFormFromConfig(data));
        updateNotification({
          id: nid,
          loading: false,
          title: "Settings saved",
          message: "Your changes were saved.",
          color: "teal",
          autoClose: 4000,
          disallowClose: false,
        });
      })
      .catch((e) => {
        setConfig(prevSnapshot);
        setForm(syncFormFromConfig(prevSnapshot));
        updateNotification({
          id: nid,
          loading: false,
          title: "Save failed",
          message: humanError(e),
          color: "red",
          autoClose: 8000,
          disallowClose: false,
        });
      })
      .finally(() => setSaving(false));
  };

  if (loading) {
    return (
      <Center p="xl">
        <Loader />
      </Center>
    );
  }

  if (!config && error) {
    return (
      <Alert color="red" title="Configuration">
        {error}
      </Alert>
    );
  }

  if (!config) {
    return <Text color="dimmed">Unable to load configuration.</Text>;
  }

  return (
    <Grid>
      {error && (
        <Grid.Col span={12}>
          <Alert color="red" withCloseButton onClose={() => setError(null)}>
            {error}
          </Alert>
        </Grid.Col>
      )}
      <Grid.Col span={6}>
        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <Title order={4} mb="md">
            Bot & widget
          </Title>
          <Group position="apart" mb="md">
            <div>
              <Text>ResiBot enabled</Text>
              <Text size="xs" color="dimmed">
                {config.enabled
                  ? "Guests can use the chat widget on your site when this is on."
                  : "Bot responses and the widget are off for this property."}
              </Text>
            </div>
            <Switch
              checked={config.enabled}
              onChange={handleEnabledToggle}
              disabled={toggling}
              size="lg"
              color="teal"
            />
          </Group>
          <Textarea
            label="Welcome message"
            minRows={3}
            value={form.welcome_message}
            onChange={(e) => setForm((f) => ({ ...f, welcome_message: e.target.value }))}
            mb="md"
          />
          <TextInput
            label="WhatsApp number"
            value={form.whatsapp_number}
            onChange={(e) => setForm((f) => ({ ...f, whatsapp_number: e.target.value }))}
            mb="md"
            icon={<IconBrandWhatsapp size={16} />}
          />
          <TextInput
            label="Calendar URL (ICS)"
            value={form.calendar_url}
            onChange={(e) => setForm((f) => ({ ...f, calendar_url: e.target.value }))}
            mb="md"
          />
          <Textarea
            label="Fallback message"
            description="Shown when the bot escalates or cannot answer."
            minRows={2}
            value={form.fallback_message}
            onChange={(e) => setForm((f) => ({ ...f, fallback_message: e.target.value }))}
            mb="md"
          />
          <Select
            label="Locale"
            data={[
              { value: "en", label: "English" },
              { value: "nl", label: "Nederlands" },
              { value: "de", label: "Deutsch" },
            ]}
            value={form.locale}
            onChange={(v) => setForm((f) => ({ ...f, locale: v || "en" }))}
            mb="md"
          />
          <Button color="teal" onClick={handleSave} loading={saving} disabled={toggling}>
            Save bot settings
          </Button>
        </Paper>
      </Grid.Col>
      <Grid.Col span={6}>
        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <Title order={4} mb="md">
            Branding
          </Title>
          <TextInput
            label="Property name"
            value={form.accommodation_name}
            onChange={(e) => setForm((f) => ({ ...f, accommodation_name: e.target.value }))}
            mb="md"
          />
          <TextInput
            label="Primary color"
            value={form.primary_color}
            onChange={(e) => setForm((f) => ({ ...f, primary_color: e.target.value }))}
            mb="md"
          />
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 8,
              backgroundColor: form.primary_color || "#4a8078",
              border: "1px solid #ddd",
            }}
          />
          <Title order={5} mt="xl" mb="sm">
            Widget plugins
          </Title>
          <Text size="xs" color="dimmed" mb="xs">
            What your public site widget loads (read-only).
          </Text>
          <Group spacing="xs">
            {(widgetInfo?.enabled_plugins || []).length === 0 ? (
              <Text size="sm" color="dimmed">
                No plugins listed for this tenant.
              </Text>
            ) : (
              widgetInfo.enabled_plugins.map((p) => (
                <Badge key={p} variant="outline" color="teal">
                  {p}
                </Badge>
              ))
            )}
          </Group>
        </Paper>
      </Grid.Col>
      <Grid.Col span={12}>
        <Paper shadow="sm" p="lg" radius="md" withBorder>
          <Group position="apart" mb="sm">
            <Title order={4}>
              Knowledge base (RAG)
            </Title>
            <Badge leftSection={<IconDatabase size={14} />} variant="light">
              Background sync
            </Badge>
          </Group>
          <Text size="sm" color="dimmed" mb="md">
            Rebuilds search content from your connected sources. The server must have data-source
            settings configured.
          </Text>
          <Group mb="md">
            <Button
              leftIcon={<IconDatabase size={18} />}
              loading={ingestLoading}
              onClick={triggerKnowledgeRefresh}
              color="teal"
            >
              Refresh knowledge base
            </Button>
            <Button variant="default" onClick={loadRagStatus}>
              Refresh status
            </Button>
          </Group>
          {ingestMessage && (
            <Alert color="blue" mb="sm" onClose={() => setIngestMessage(null)} withCloseButton>
              {ingestMessage}
            </Alert>
          )}
          {ragStatus && (
            <Text size="sm">
              <strong>Last job:</strong> {ragStatus.status || "—"} · chunks {ragStatus.chunk_count ?? 0} ·
              sources {ragStatus.source_count ?? 0}
              {ragStatus.error ? (
                <>
                  {" "}
                  <Badge color="red">Error</Badge> {ragStatus.error}
                </>
              ) : null}
            </Text>
          )}
        </Paper>
      </Grid.Col>
    </Grid>
  );
}

const ResiBotManager = () => {
  const { request, tenantId, setTenantId, allowedTenantIds } = useResibotApi();
  const envOverride = Boolean(
    process.env.REACT_APP_RESIBOT_TENANT_ID && String(process.env.REACT_APP_RESIBOT_TENANT_ID).trim()
  );

  return (
    <Container size="xl" py="xl">
      <Group position="apart" mb="xl" align="flex-start">
        <div>
          <Title order={2}>ResiBot Manager</Title>
        </div>
        <Group spacing="sm" align="flex-end">
          {allowedTenantIds.length > 1 && !envOverride ? (
            <Select
              label="Tenant workspace"
              description="Your choice is saved in this browser"
              placeholder="Select tenant"
              value={tenantId}
              onChange={(v) => v && setTenantId(v)}
              data={allowedTenantIds.map((t) => ({ value: t, label: t }))}
              w={280}
              size="sm"
            />
          ) : (
            <Badge size="lg" color="teal" variant="filled">
              Tenant: {tenantId}
            </Badge>
          )}
        </Group>
      </Group>

      <Tabs defaultValue="overview">
        <Tabs.List mb="xl">
          <Tabs.Tab value="overview" icon={<IconChartBar size={16} />}>
            Overview
          </Tabs.Tab>
          <Tabs.Tab value="queries" icon={<IconList size={16} />}>
            Query log
          </Tabs.Tab>
          <Tabs.Tab value="faqs" icon={<IconHelp size={16} />}>
            FAQs
          </Tabs.Tab>
          <Tabs.Tab value="contacts" icon={<IconUsers size={16} />}>
            Contacts
          </Tabs.Tab>
          <Tabs.Tab value="cleaning" icon={<IconBrush size={16} />}>
            Cleaning
          </Tabs.Tab>
          <Tabs.Tab value="settings" icon={<IconSettings size={16} />}>
            Settings
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="overview">
          <OverviewTab request={request} />
        </Tabs.Panel>

        <Tabs.Panel value="queries">
          <QueriesTab request={request} tenantId={tenantId} />
        </Tabs.Panel>

        <Tabs.Panel value="faqs">
          <FaqsTab request={request} tenantId={tenantId} />
        </Tabs.Panel>

        <Tabs.Panel value="contacts">
          <ContactsTab request={request} tenantId={tenantId} />
        </Tabs.Panel>

        <Tabs.Panel value="cleaning">
          <CleaningTab request={request} tenantId={tenantId} />
        </Tabs.Panel>

        <Tabs.Panel value="settings">
          <SettingsTab request={request} tenantId={tenantId} />
        </Tabs.Panel>
      </Tabs>
    </Container>
  );
};

export default ResiBotManager;
